import { useMutation, useQuery } from '@tanstack/react-query'

import { StorageService } from '@/services/core/storage'
import { NotificationsApi } from '@/services/notifications/notificationsApi'
import { useCurrentUser } from '../contexts/UserContext'
import { useUserProfile } from '../contexts/UserProfileContext'
import { useNetwork } from '../contexts/NetworkContext'

/** How often to look for new notifications — the old UI's interval. */
const POLL_INTERVAL_MS = 45_000

/**
 * The signed-in user's system notifications on the current network, which of
 * them are unread, and ways to mark them all read or clear them away.
 */
export function useSystemNotifications() {
  const user = useCurrentUser()
  const { currentNetwork } = useNetwork()
  const { profile, latest, save } = useUserProfile()
  const tagId = currentNetwork?.tagId
  // Support staff acting as someone else must not read their notifications for them.
  const isMasquerading = !!StorageService.getMasquerade()
  const marks = profile ? NotificationsApi.marksOf(profile) : undefined

  const since = (tagId && marks?.since[tagId]) || null
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', tagId, since],
    queryFn: () => NotificationsApi.query(since),
    enabled: !!user && !!tagId && !!marks,
    retry: false,
    // A user without access to notifications gets an error; stop asking.
    refetchInterval: (query) => (query.state.status === 'error' ? false : POLL_INTERVAL_MS),
  })

  const readTime = Date.parse((tagId && marks?.read[tagId]) || '')
  const unread = notifications.filter(
    (n) => !Number.isFinite(readTime) || Date.parse(n.time) > readTime
  )

  const mark = useMutation({
    mutationFn: async (clear: boolean) =>
      save(NotificationsApi.markValues(await latest(), tagId!, clear)),
  })

  const canMark = !!user && !!tagId && !isMasquerading
  return {
    notifications,
    unread,
    isLoading,
    markRead: () => {
      if (canMark && unread.length > 0) mark.mutate(false)
    },
    clear: () => {
      if (canMark) mark.mutate(true)
    },
    canClear: canMark,
  }
}
